// src/services/sectionBlocks.ts
// -----------------------------------------------------------------------------
// Pure logic for the seasonal "section availability" blocks: near year-end,
// Zephyr stops offering some request sections (e.g. Devices, Uniforms) because
// spend so close to the school year's end is often wasted. Admin creates a block
// per section — a date window + message (Manage → Section Availability, stored in
// the public.section_blocks table, db-39). While the window is active the request
// wizard refuses to tick that section and shows the message instead (and un-ticks
// it, with the message, when an older draft is reopened).
// Dates are EXPLICIT (set each year, no automatic recurrence — Dev Team call,
// 2 July 2026) and compared as LOCAL calendar dates, inclusive on both ends.
//
// Status is DERIVED from the dates (+ the deactivated flag), never stored — so an
// expired block self-files into history with no job to move it. See blockStatus /
// blockEditability below and docs/section-availability-table-plan.md.

export type SectionKey = "fees" | "uniforms" | "shoes" | "laptop";

export interface SectionBlock {
  section: SectionKey;
  start: string; // "YYYY-MM-DD", inclusive (local date)
  end: string; // "YYYY-MM-DD", inclusive (local date)
  message: string; // shown to the shelter in the modal
}

// The four wizard sections, in display order (labels match the wizard's
// "What does this child need?" checklist).
export const SECTION_BLOCK_SECTIONS: { key: SectionKey; label: string }[] = [
  { key: "fees", label: "School Costs & Stationery" },
  { key: "uniforms", label: "Uniforms" },
  { key: "shoes", label: "Shoes" },
  { key: "laptop", label: "Device" },
];

export function sectionBlockLabel(section: string): string {
  const s = SECTION_BLOCK_SECTIONS.find((x) => x.key === section);
  return s ? s.label : section;
}

function localDateString(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// The active block for `section` on the local calendar date of `now`, or null.
// A block only counts when fully specified (both dates + a non-blank message);
// a half-filled admin row can never lock a section. ISO date strings compare
// correctly as plain strings, so no Date parsing is needed.
// The wizard's per-child shape, as far as the blocks care: the four section
// slots (fees is an array + an open flag; the other three are object-or-null).
export interface ChildSectionsLike {
  feesOpen?: boolean;
  fees?: unknown[] | null;
  uniforms?: unknown;
  shoes?: unknown;
  laptop?: unknown;
}

// Remove any currently-blocked sections from a draft's children (the reopen-a-
// draft path: the tick is removed and the section's entries discarded, per the
// Dev Team call 2 July 2026). Pure — returns new child objects only where a
// change was needed, plus the distinct blocks that caused removals (for the
// one explanatory modal). Persisted only when the wizard next saves.
export function stripBlockedSections<T extends ChildSectionsLike>(
  children: T[],
  blocks: SectionBlock[] | null | undefined,
  now: Date = new Date()
): { children: T[]; removed: SectionBlock[] } {
  const removed: SectionBlock[] = [];
  const seen = new Set<string>();
  const note = (b: SectionBlock) => {
    if (!seen.has(b.section)) {
      seen.add(b.section);
      removed.push(b);
    }
  };
  const out = children.map((c) => {
    const patch: Record<string, unknown> = {};
    const feesTicked = !!(c.feesOpen || (Array.isArray(c.fees) && c.fees.length > 0));
    if (feesTicked) {
      const b = activeSectionBlock(blocks, "fees", now);
      if (b) {
        patch.feesOpen = false;
        patch.fees = [];
        note(b);
      }
    }
    for (const key of ["uniforms", "shoes", "laptop"] as const) {
      if (c[key] !== null && c[key] !== undefined) {
        const b = activeSectionBlock(blocks, key, now);
        if (b) {
          patch[key] = null;
          note(b);
        }
      }
    }
    return Object.keys(patch).length > 0 ? { ...c, ...patch } : c;
  });
  return { children: out, removed };
}

export function activeSectionBlock(
  blocks: SectionBlock[] | null | undefined,
  section: string,
  now: Date = new Date()
): SectionBlock | null {
  if (!Array.isArray(blocks)) return null;
  const today = localDateString(now);
  return (
    blocks.find(
      (b) =>
        !!b &&
        b.section === section &&
        typeof b.start === "string" &&
        b.start !== "" &&
        typeof b.end === "string" &&
        b.end !== "" &&
        typeof b.message === "string" &&
        b.message.trim() !== "" &&
        b.start <= today &&
        today <= b.end
    ) || null
  );
}

// -----------------------------------------------------------------------------
// Derived status + editability (table-backed rebuild, db-39).
// -----------------------------------------------------------------------------
// A block's status is NOT stored — it's derived from the dates + the deactivated
// flag every time it's read, so "Expired" needs no job to move a row and cancel
// (Deactivate) is a simple flag. The admin screen and the db-39 edit-guard
// trigger both follow these rules; keep them in step.

export type BlockStatus = "scheduled" | "live" | "expired" | "cancelled";

// The minimum a block needs for status/editability: its window + deactivated
// flag (the section_blocks row shape, `_date`-suffixed to match the table).
export interface DatedBlock {
  start_date: string; // "YYYY-MM-DD", inclusive (local date)
  end_date: string; // "YYYY-MM-DD", inclusive (local date)
  deactivated?: boolean;
}

// Deactivated → Cancelled; otherwise Scheduled / Live / Expired by today's local
// date (inclusive both ends).
export function blockStatus(b: DatedBlock, now: Date = new Date()): BlockStatus {
  if (b.deactivated) return "cancelled";
  const today = localDateString(now);
  if (today < b.start_date) return "scheduled";
  if (today > b.end_date) return "expired";
  return "live";
}

export interface BlockEditability {
  status: BlockStatus;
  startEditable: boolean; // only while Scheduled (start still in the future)
  endEditable: boolean; // while not expired/cancelled
  messageEditable: boolean; // while not expired/cancelled
  canDeactivate: boolean; // the cancel action — while Scheduled or Live
  // Earliest the end date may be set to = max(today, start): end dates can't be
  // back-dated (cancel is via Deactivate). "YYYY-MM-DD".
  endMinDate: string;
}

export function blockEditability(b: DatedBlock, now: Date = new Date()): BlockEditability {
  const status = blockStatus(b, now);
  const active = status === "scheduled" || status === "live";
  const today = localDateString(now);
  return {
    status,
    startEditable: status === "scheduled",
    endEditable: active,
    messageEditable: active,
    canDeactivate: active,
    endMinDate: b.start_date > today ? b.start_date : today,
  };
}
